import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
    createCustomerSavedLocation,
    deleteCustomerSavedLocation,
    listCustomerSavedLocations,
    updateCustomerSavedLocation,
} from './api'
import {savedLocationKeys} from './query-keys'
import type {CreateSavedLocationInput, UpdateSavedLocationInput} from './schemas'

export function useCustomerSavedLocations(
    customerId: string,
    search?: string,
    options?: {enabled?: boolean},
) {
    return useQuery({
        queryKey: savedLocationKeys.list(customerId, {search}),
        queryFn: () => listCustomerSavedLocations(customerId, search),
        enabled: options?.enabled ?? Boolean(customerId),
    })
}

export function useCreateCustomerSavedLocation(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateSavedLocationInput) =>
            createCustomerSavedLocation(customerId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: savedLocationKeys.lists(customerId),
            })
        },
    })
}

export function useUpdateCustomerSavedLocation(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            savedLocationId,
            input,
        }: {
            savedLocationId: string
            input: UpdateSavedLocationInput
        }) => updateCustomerSavedLocation(customerId, savedLocationId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: savedLocationKeys.lists(customerId),
            })
        },
    })
}

export function useDeleteCustomerSavedLocation(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (savedLocationId: string) =>
            deleteCustomerSavedLocation(customerId, savedLocationId),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: savedLocationKeys.lists(customerId),
            })
        },
    })
}
